import { getHelper } from '@/devtools/utils';
import { SIMPLE_CHOICE } from '@/features/ChoicesLists/Simple/types';
import { VersicherungsnehmerPropertyContractSchema } from '@/features/Clients/Schemas/Versicherungsnehmer';
import type { ComparisonDetail } from '@/features/Comparison/Components/types';
import { SEARCH_DATA_CONTEXT } from '@/features/Global/types';
import type { InputStore } from '@/features/Sparten/phv/useInputStore';
import { usePhvInputStore } from '@/features/Sparten/phv/useInputStore';
import { featureFilterFunction } from '@/features/Sparten/phv/utils/featureFilterFunction';
import type { SpartenConfig } from '@/features/Sparten/types';
import { dehydrateRequestData } from '@/utils/dehydrateRequestData/dehydrateRequestData';
import { formatCurrency, formatMillions } from '@/utils/formatters/euro';
import { formatChoice } from '@/utils/formatters/simpleChoice';
import { formatYears } from '@/utils/formatters/years';
import { useStorableState } from '@/utils/useStorableState/useStorableState';
import { phvInputDehydrationDefinitions } from '@/features/Sparten/phv/inputDehydration';
import {
  KEEP_DATA_FROM_QUOTE_MAPPING,
  keepDataFromQuoteMappings,
} from '@/features/Sparten/shared/keepDataFromQuoteMappings';
import boolean from '@/utils/formatters/boolean';
import { isTarifgruppeBeamterReactive } from '@/features/Sparten/phv/formConfigs/sharedRenderConditions';
import { ENTITY_TYPE } from '@/features/EntityManager/types';
import image from './assets/bg.jpg';
import { phvVersicherungsangabenConfig } from '@/features/Sparten/phv/versicherungsangabenConfig';

export const config: SpartenConfig = {
  component: () => import('@/views/Base/Layout.vue'),
  path: '/phv',

  props: {
    name: 'Privathaftpflichtversicherung',
    sparte: 'phv',
    image,
  },

  general: {
    distribution: {
      channel: true,
      pooling: true,
    },

    maxSimultaneousCount: 3,

    hasRisikoDEMVerCertificate: true,

    versicherungsangabenConfig: phvVersicherungsangabenConfig,

    clientFields: {
      versicherungsnehmer: {
        geburtsdatum: 'geburtsdatum',
        plz: 'adresse.plz',
      },
      versichertePerson: {
        geburtsdatum: 'geburtsdatum',
        plz: 'adresse.plz',
      },
    },

    calculationRelevantEntityFields: {
      [ENTITY_TYPE.CLIENT]: {
        versicherungsnehmer: ['geburtsdatum', 'adresse.plz'],
        versichertePerson: ['geburtsdatum', 'adresse.plz'],
      },
    },

    profileFields: ['deckungssumme', 'selbstbeteiligung', 'tarifart'],

    keepDataFromQuote: {
      clients: {
        versicherungsnehmer: [
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.BIRTHDAY],
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.ZIP_CODE],
        ],
      },
    },

    supportMitversichertenPartner: true,

    featureFilterFunction,

    buildInputDataObject: (context: SEARCH_DATA_CONTEXT, dehydrate = false) => {
      const state = usePhvInputStore().$state as InputStore;

      const preparedData = useStorableState().create(state);

      if (context === SEARCH_DATA_CONTEXT.TARIFF && dehydrate) {
        return dehydrateRequestData(
          preparedData,
          phvInputDehydrationDefinitions,
        );
      }

      return preparedData;
    },

    buildContractDataObject: (state) => state,

    verifyZipcodeCityCombination: true,

    comparison: {
      getDetails: () => {
        const map = new Map<string, ComparisonDetail>();

        map.set('betrag', {
          label: 'zu zahlender Beitrag',
          format: formatCurrency,
        });

        return map;
      },
      serviceFields: [
        {
          label: 'Laufzeit',
          fieldPath: 'laufzeit',
          formatter: formatYears,
        },
        {
          label: 'Deckungssumme',
          fieldPath: 'deckungssumme',
          inputFormatter: formatChoice(SIMPLE_CHOICE.DECKUNGSSUMME),
          formatter: formatMillions,
        },
        {
          label: 'Selbstbeteiligung',
          fieldPath: 'selbstbeteiligung',
          formatter: formatCurrency,
          inputFormatter: formatChoice(SIMPLE_CHOICE.SELBSTBETEILIGUNG),
        },
        {
          label: 'Zahlweise',
          fieldPath: 'zahlweise',
          formatter: formatChoice(SIMPLE_CHOICE.PAYMENT_PERIOD),
        },
        {
          label: 'Diensthaftpflicht',
          fieldPath: 'diensthaftpflicht',
          formatter: boolean,
          inputFieldAsFallback: true,
          requirement: () => isTarifgruppeBeamterReactive.value,
        },
      ],
    },

    baseTariffComponent: () =>
      import('@/features/Tariffs/BaseTariff/BaseTariff.vue'),

    tariffComponent: () =>
      import(
        '@/features/Sparten/phv/components/TariffDetails/TariffDetails.vue'
      ),

    devtools: {
      directAccess: {
        input: {
          icon: 'edit',
          setup: async () => getHelper('phv').resetState(),
        },
        offers: {
          icon: 'list',
          setup: async () => getHelper('phv').selectAllFeatures(),
        },
        comparison: {
          icon: 'compare_arrows',
          setup: async () => getHelper('phv').loadTariffs(),
        },
        contract: {
          icon: 'description',
          setup: async () => getHelper('phv').showContract(),
        },
      },
    },

    contractVnSchema: () => VersicherungsnehmerPropertyContractSchema,
    blockCompanyClient: true,
  },

  // Wizard steps
  steps: {
    input: {
      component: () => import('@/features/Sparten/phv/views/InputView.vue'),
    },

    offers: {
      component: () => import('@/features/Sparten/phv/views/Tariffs.vue'),
    },

    comparison: {
      component: () => import('@/features/Sparten/phv/views/Comparison.vue'),
    },

    contract: {
      component: () => import('@/features/Sparten/phv/views/ContractView.vue'),
    },

    completion: {
      component: () => import('@/views/Layouts/Completion/Completion.vue'),
    },
  },
};

export default config;
